/**
 * 63.1o, the technical as fire support (the coordinator's brief, on the owner's technical — a light truck with a .50 in its
 * bed, three seats: driver, the gun, a cab passenger): not a transport. A driver (and a gunner, the idle gun rule brings
 * one) takes it to a firing position overlooking the objective at the .50's reach, stops there with the gun's line on it,
 * and backs off a hull when hit or when an enemy closes; it never carries a squad to a dismount. Pure readers here; the
 * brain's half is ai/actionBot.ts (the mounted branch's technical driver, and the idle rule that sends a driver).
 */
import type { GameState, StructureState, Vec3 } from '../types';
import { vecAdd, vecNormalize, vecScale, vecSub, vecLength, vecCross } from '../math';
import { dcos, dsin } from '../core/dmath';
import { arcDistance } from '../core/gameState';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { personLineOfSight, unitPointAt } from '../core/fineTerrain';
import { projectToTangent } from '../core/surfaceWalk';
import { squadTaskFor } from '../core/squads';
import { seatUnit } from '../core/seats';
import { weatherEffectHere } from '../core/weather';
import { sitesAsKnown } from '../core/objectiveIntel';
import { scoutOf, scoutVehicle, SCOUT_REACH } from './scoutRide';
import { orderPlace } from '../core/orderForms';

/** 63.1r: how near a squad must be to a technical to crew it (people). */
export const TECH_CREW_PEOPLE = 60;
/** 69.14: a vehicle within this of its side's base (people) is at the base — where the motor pool unpacks a replacement (the APC loop's BASE_PARTY_PEOPLE). */
export const TECH_BASE_PEOPLE = 120;

/** The side's base (its board base, else its home). */
const baseOf = (state: GameState, team: number): Vec3 | null => state.board?.bases.find(b => b.team === team)?.dir ?? state.actionHomes?.[team] ?? null;

/**
 * 69.14 (lane E's finding: the replacement the motor pool unpacks mid-match stood at the pad uncrewed on every seed): a
 * technical standing at its side's base is crewed by a squad's party there — its living members on foot within
 * TECH_BASE_PEOPLE of the base, the arrivals — not by the whole squad: the crew is the party's (by id), and its squadmates
 * away at the front are not sent back for it. Null: the whole squad's (the technical away from the base, or no party).
 */
export function crewParty(state: GameState, team: number, tech: StructureState, unitIds: readonly number[]): number[] | null {
  const base = baseOf(state, team), R = state.planets[tech.planetIndex].radius, person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  if (!base || arcDistance(tech.surfaceDir, base, R) > TECH_BASE_PEOPLE * person) return null;
  const ms = unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is NonNullable<typeof p> => !!p && p.alive && p.planetIndex === tech.planetIndex);
  const party = ms.filter(p => (!p.mount || p.mount.bodyId === tech.id) && arcDistance(p.surfaceDir, base, R) <= TECH_BASE_PEOPLE * person).map(p => p.id).sort((a, b) => a - b);
  return party.length && party.length < ms.length ? party : null;
}

/**
 * 69.14: does `team` want a crew at its base — an empty vehicle of its that drives standing there (a replacement unpacked at
 * the pool, a ferry back) with none of its people within TECH_CREW_PEOPLE? In the push the waves land forward, nobody came
 * back, and the replacement stood 222 s on the median before anyone boarded, three of twelve never. The commander's arrivals
 * come to the base while it does (ai/squadOrders.ts, as while it masses).
 */
export function crewWanted(state: GameState, team: number): boolean {
  const base = baseOf(state, team);
  if (!base) return false;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  return state.structures.some(v => {
    if (v.health <= 0 || !(v.type === 'technical' || v.type === 'wheeled' || v.type === 'tracked' || v.type === 'tank') || state.players.find(p => p.id === v.ownerId)?.teamIndex !== team) return false;
    const R = state.planets[v.planetIndex].radius;
    if (arcDistance(v.surfaceDir, base, R) > TECH_BASE_PEOPLE * person || (v.seats ?? []).some(x => seatUnit(x) !== null)) return false;
    return !state.players.some(p => p.alive && p.teamIndex === team && p.planetIndex === v.planetIndex && arcDistance(p.surfaceDir, v.surfaceDir, R) <= TECH_CREW_PEOPLE * person);
  });
}

/**
 * 63.1r (the owner: "technicals don't seem to get used like apcs do … only gunner seems to load and use by default for
 * technical"): the technical crewed as a squad's decision, as the APC's ride is — each standing technical of `team` goes to
 * one squad of bots with no task, none of it seated elsewhere, the nearest within TECH_CREW_PEOPLE (a squad already aboard
 * keeps it); two of the squad crew it, the driver and the gun (actionBot.ts TECH_CREW; the third fights on foot). The idle rule's driver (the nearest idle bot) was rarely
 * there: one team's technical on fh-1 had a driver 38 s of 600, its gunner 598. Read off the tick's start. Squad id →
 * the technical's id.
 */
export function technicalCrews(state: GameState, team: number, tick: number): Map<number, number> {
  const out = new Map<number, number>();
  const teamOf = (id: number) => state.players.find(p => p.id === id)?.teamIndex;
  const techs = state.structures.filter(s => (s.type === 'technical' || s.armament === 'mortar' || s.type === 'tank') && s.health > 0 && teamOf(s.ownerId) === team).sort((a, b) => a.id - b.id);   // (69.23: and the mortar carrier, crewed the same — its driver and its tube; 69.29 step 5: the tank — its driver, its gunner and its commander)
  if (!techs.length) return out;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const squads = (state.squads ?? []).filter(sq => sq.team === team).sort((a, b) => a.id - b.id);
  const membersOf = (sq: { unitIds: number[] }) => sq.unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is NonNullable<typeof p> => !!p && p.alive);
  const taken = new Set<number>();
  for (const t of techs) {
    const R = state.planets[t.planetIndex].radius;
    // (a squad aboard keeps its technical)
    // (69.23: a mortar carrier's is the squad at its wheel, else at its tube — one at its .50 only does not keep it: an idle
    // rifle of another squad at the .50 took it from its crew mid-drive, and its own squad, 270 people off, never came)
    const keepSeats = t.armament === 'mortar' ? [(t.seats ?? []).find(x => x.role === 'driver'), (t.seats ?? []).find(x => x.mortar)]
      : t.type === 'tank' ? [(t.seats ?? []).find(x => x.role === 'driver'), (t.seats ?? []).find(x => x.main)] : (t.seats ?? []);   // (69.29 step 5: a tank's is the squad at its wheel, else at its gun)
    const aboard = keepSeats.map(x => (x ? seatUnit(x) : null)).filter((id): id is number => id !== null);
    let pick = (t.armament === 'mortar' || t.type === 'tank' ? aboard.map(id => squads.find(sq => !taken.has(sq.id) && sq.unitIds.includes(id))).find(sq => !!sq) : squads.find(sq => !taken.has(sq.id) && aboard.some(id => sq.unitIds.includes(id)))) ?? null;
    if (!pick) {
      let best = Infinity;
      for (const sq of squads) {
        if (taken.has(sq.id)) continue;
        const ms = membersOf(sq);
        if (ms.length < 2 || ms.some(p => !p.isBot || state.controllers?.[p.id] !== undefined || p.planetIndex !== t.planetIndex)) continue;
        // (a task sits on top — save the commander's scout, whose vehicle by the owner's order is this: 63.1t, scoutRide.ts)
        const tasks = ms.map(p => squadTaskFor(state, p.id, tick));
        const scouting = tasks.every(t => t !== null && scoutOf(t) !== null) && scoutVehicle(state, ms, tick, SCOUT_REACH.bikePeople * person, SCOUT_REACH.techPeople * person) === 'technical';
        if (ms.some(p => p.mount) || (tasks.some(t => t !== null) && !scouting)) continue;
        const party = crewParty(state, team, t, sq.unitIds);   // (69.14: at the base, the squad's party there)
        const d = Math.min(...ms.filter(p => !party || party.includes(p.id)).map(p => arcDistance(p.surfaceDir, t.surfaceDir, R)));
        if (d <= TECH_CREW_PEOPLE * person && d < best) { best = d; pick = sq; }
      }
    }
    if (pick) { taken.add(pick.id); out.set(pick.id, t.id); }
  }
  return out;
}

/** The post's distance from the objective (people): inside the .50's reach (about 62 people), its cone tight enough there. */
export const TECH_POST_PEOPLE = 50;
/** fix/63.1r-post-on-enemy: the post within this share of the weather's sight at what it covers (clear weather: unlimited, so TECH_POST_PEOPLE). */
export const TECH_POST_SIGHT = 0.75;
/** The bearings tried round the objective, off the line from it toward the technical (radians). */
const TECH_POST_SWINGS = [0, 0.35, -0.35, 0.7, -0.7, 1.05, -1.05];
/** The gun's eye over the ground at the post (people): the bed's gunner standing at his gun. */
const TECH_EYE_PEOPLE = 1.9;
/** How near the post counts as there (people). */
export const TECH_AT_POST_PEOPLE = 4;
/** An enemy known this near the technical, or a hit, backs it off (people). */
export const TECH_CLOSE_PEOPLE = 20;

/** The objective a technical of `team` covers: the nearest capture point its side does not hold, else null. */
export function technicalObjective(state: GameState, tech: StructureState, team: number): Vec3 | null {
  // 69.37 step 1 (ARCHITECTURE §5.28): a fire-support vehicle a live mounted assault names covers that play's objective
  const play = (state.plays ?? []).find(p => p.phase !== 'done' && p.team === team && p.mounted?.fire.includes(tech.id));
  if (play) {
    const o = play.objective, place = 'pointId' in o && o.pointId !== undefined ? orderPlace(state, { kind: 'hold', pointId: o.pointId }) : orderPlace(state, { kind: 'hold', ...(o as { planetIndex: number; surfaceDir: Vec3 }) });
    if (place && place.planetIndex === tech.planetIndex) return place.surfaceDir;
  }
  const R = state.planets[tech.planetIndex]?.radius ?? 1;
  const sites = sitesAsKnown(state, team, state.modeState.points?.sites ?? [], 0).filter(s => s.planetIndex === tech.planetIndex && s.owner !== team);   // (69.2a: as its side knows them — its place and holder, which no tick moves)
  if (!sites.length) return null;
  return sites.reduce((a, b) => arcDistance(b.surfaceDir, tech.surfaceDir, R) < arcDistance(a.surfaceDir, tech.surfaceDir, R) ? b : a).surfaceDir;
}

/**
 * fix/63.1r-post-on-enemy (62.58 step 6's grid: a technical at its post with enemies 45–75 people off fired nothing — the
 * post looked at the objective, and the enemy was seldom on it): what the technical covers — the nearest enemy its team
 * knows within `reach` of it (world units, the .50's), else its objective.
 */
export function technicalGoal(state: GameState, tech: StructureState, team: number, known?: ReadonlyArray<{ planetIndex: number; surfaceDir: Vec3 }>, reach = 0): Vec3 | null {
  const R = state.planets[tech.planetIndex]?.radius ?? 1;
  let best: Vec3 | null = null, bestD = Infinity;
  for (const k of known ?? []) {
    if (k.planetIndex !== tech.planetIndex) continue;
    const d = arcDistance(k.surfaceDir, tech.surfaceDir, R);
    if (d <= reach && d < bestD) { bestD = d; best = k.surfaceDir; }
  }
  return best ?? technicalObjective(state, tech, team);
}

/**
 * Where a technical of `team` sets up: TECH_POST_PEOPLE from what it covers (`technicalGoal`: a known enemy within the
 * .50's reach, else its objective) — nearer, TECH_POST_SIGHT of the weather's sight there at `seconds`, where that is less
 * (fog: a gun 50 people off saw nothing at 16–20) — on the side toward it, the first bearing whose gun eye sees a
 * person's eye there (a line over the fine ground); with none, the first bearing. Null with nothing to cover.
 */
export function technicalPost(state: GameState, tech: StructureState, team: number, known?: ReadonlyArray<{ planetIndex: number; surfaceDir: Vec3 }>, reach = 0, seconds = 0): { planetIndex: number; surfaceDir: Vec3 } | null {
  const goal = technicalGoal(state, tech, team, known, reach);
  const planet = state.planets[tech.planetIndex];
  if (!goal || !planet) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const g = vecNormalize(goal), toTech = projectToTangent(vecSub(vecNormalize(tech.surfaceDir), g), g);
  const base = vecLength(toTech) > 1e-9 ? vecNormalize(toTech) : vecNormalize(vecCross(g, { x: 0, y: 1, z: 0 }));
  const side = vecNormalize(vecCross(g, base));
  const arc = Math.min(TECH_POST_PEOPLE * person, TECH_POST_SIGHT * weatherEffectHere(state, tech.planetIndex, goal, seconds).sight * ps.k) / planet.radius;
  const target = unitPointAt(planet, g, ps, ps.eyeHeight);
  let first: Vec3 | null = null;
  for (const a of TECH_POST_SWINGS) {
    const dir = vecNormalize(vecAdd(vecScale(base, dcos(a)), vecScale(side, dsin(a))));
    const at = vecNormalize(vecAdd(vecScale(g, dcos(arc)), vecScale(dir, dsin(arc))));
    first ??= at;
    const eye = unitPointAt(planet, at, ps, TECH_EYE_PEOPLE * person);
    if (personLineOfSight(eye, target, state)) return { planetIndex: tech.planetIndex, surfaceDir: at };
  }
  return first ? { planetIndex: tech.planetIndex, surfaceDir: first } : null;
}

/**
 * 63.1r, the technical's role as a scout (GameConfig.technicalRole, on unless false): a technical crewed by a scouting squad
 * (63.1t's scouts ride) drives to the scout's place — the sweep's spot in its circle (69.5a) — rather than to a gun's post
 * TECH_POST_PEOPLE off the circle's centre, and its whole squad rides (actionBot: up to its seats, the cab rider boarding once
 * the driver and the gunner are in). Measured on the Foothold tug, objectives known roughly: circles resolved in a median
 * 127.5 s against 148.5 s. (Its assault half — a play's support squad as its crew, its post 30 people from the objective —
 * was measured and dropped: the same takes, more technicals lost; BUILDLIST 63.1r.) Any other crew: the post as before.
 */
export function technicalPostFor(state: GameState, tech: StructureState, team: number, squadId: number | null, tick: number, known?: ReadonlyArray<{ planetIndex: number; surfaceDir: Vec3 }>, reach = 0, seconds = 0): { planetIndex: number; surfaceDir: Vec3 } | null {
  if (squadId !== null && state.config.technicalRole !== false) {
    const sq = (state.squads ?? []).find(q => q.id === squadId);
    const scout = sq ? sq.unitIds.map(id => scoutOf(squadTaskFor(state, id, tick))).find(o => o !== null) ?? null : null;
    if (scout && scout.planetIndex === tech.planetIndex) return { planetIndex: scout.planetIndex, surfaceDir: scout.surfaceDir };
  }
  return technicalPost(state, tech, team, known, reach, seconds);
}

/** 69.23: a mortar carrier's post behind its objective (people): its reach about 640 (MORTAR.speedPeople's high solution), so ~440 past the objective. */
export const MORTAR_POST_PEOPLE = 200;
/** 69.23: set up, it moves to a new post only this far off (people): each move packs the tube and sets it again (MORTAR.setupSeconds). */
export const MORTAR_REPOST_PEOPLE = 80;

/**
 * 69.23 (DECISIONS 241: the crew's job is the post, the set-up and answering calls): where a mortar carrier of `team` sets
 * up — MORTAR_POST_PEOPLE behind the objective it serves (`technicalObjective`: the nearest point its side does not hold),
 * toward its side's base (at the base, when that is nearer). Null with nothing to serve.
 */
export function mortarPost(state: GameState, carrier: StructureState, team: number): { planetIndex: number; surfaceDir: Vec3 } | null {
  const obj = technicalObjective(state, carrier, team), planet = state.planets[carrier.planetIndex];
  if (!obj || !planet) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, home = baseOf(state, team);
  const g = vecNormalize(obj), back = projectToTangent(vecSub(vecNormalize(home ?? carrier.surfaceDir), g), g);
  if (vecLength(back) < 1e-9) return { planetIndex: carrier.planetIndex, surfaceDir: g };
  const gap = arcDistance(g, vecNormalize(home ?? carrier.surfaceDir), planet.radius);
  const arc = Math.min(MORTAR_POST_PEOPLE * person, gap) / planet.radius, dir = vecNormalize(back);
  return { planetIndex: carrier.planetIndex, surfaceDir: vecNormalize(vecAdd(vecScale(g, dcos(arc)), vecScale(dir, dsin(arc)))) };
}
